import MapboxDraw from "@mapbox/mapbox-gl-draw";

export const DEFAULT_DRAW_COLOR = "#0BA5EC";
export const DEFAULT_DRAW_LINE_WIDTH = 2;

/** Minimum white-ring radius for a vertex (px). */
const VERTEX_OUTER_MIN_PX = 10;
/** Extra radius added on top of line width (px). */
const VERTEX_OUTER_PADDING_PX = 6;
/** How much vertex radius grows with stroke width. */
const VERTEX_OUTER_PER_LINE_PX = 0.9;
/** Inner fill is this many px smaller than the white ring. */
const VERTEX_INNER_INSET_PX = 4;
const VERTEX_INNER_MIN_PX = 8;
const VERTEX_MIDPOINT_MIN_PX = 7;
/** Extra radius while a handle is active / hovered. */
const VERTEX_ACTIVE_GROW_PX = 2;

type DrawStyle = {
	[key: string]: unknown;
	filter?: unknown;
	id: string;
	layout?: Record<string, unknown>;
	paint?: Record<string, unknown>;
	type: string;
};

const drawTheme = (
	MapboxDraw as typeof MapboxDraw & { lib: { theme: DrawStyle[] } }
).lib.theme;

export const drawHandleRadii = (lineWidth: number) => {
	const outer = Math.max(
		VERTEX_OUTER_MIN_PX,
		Math.round(lineWidth * VERTEX_OUTER_PER_LINE_PX + VERTEX_OUTER_PADDING_PX),
	);
	const inner = Math.max(VERTEX_INNER_MIN_PX, outer - VERTEX_INNER_INSET_PX);
	return {
		inner,
		innerActive: inner + VERTEX_ACTIVE_GROW_PX,
		midpoint: Math.max(VERTEX_MIDPOINT_MIN_PX, inner - 1),
		outer,
		outerActive: outer + VERTEX_ACTIVE_GROW_PX,
	};
};

const vertexFilterVisibleWhenSelected = (filter: unknown): unknown => {
	if (!Array.isArray(filter) || filter[0] !== "all") return filter;
	return filter.filter(
		(clause) =>
			!(
				Array.isArray(clause) &&
				clause[0] === "!=" &&
				clause[1] === "mode" &&
				clause[2] === "simple_select"
			),
	);
};

/** Build Mapbox Draw theme styles tinted to the zone swatch and stroke width. */
export const createDrawStyles = (
	color: string,
	lineWidth: number,
	fillOpacity = 0,
): DrawStyle[] | undefined => {
	if (!drawTheme?.length) return undefined;

	const radii = drawHandleRadii(lineWidth);

	return drawTheme.map((style) => {
		const paint: Record<string, unknown> = { ...style.paint };
		const next: DrawStyle = { ...style, paint };

		if (style.id.includes("vertex")) {
			next.filter = vertexFilterVisibleWhenSelected(style.filter);
		}

		if (style.type === "fill") {
			paint["fill-color"] = color;
			paint["fill-opacity"] = fillOpacity;
		}

		if (style.type === "line") {
			paint["line-color"] = color;
			paint["line-dasharray"] = [1, 0];
			paint["line-width"] = lineWidth;
			next.layout = {
				...style.layout,
				"line-cap": "round",
				"line-join": "round",
			};
		}

		if (style.type === "circle") {
			if (style.id.includes("midpoint")) {
				paint["circle-radius"] = radii.midpoint;
			} else if (style.id.includes("outer")) {
				paint["circle-radius"] = [
					"case",
					["==", ["get", "active"], "true"],
					radii.outerActive,
					radii.outer,
				];
			} else {
				paint["circle-radius"] = [
					"case",
					["==", ["get", "active"], "true"],
					radii.innerActive,
					radii.inner,
				];
			}

			if (!style.id.includes("outer")) {
				paint["circle-color"] = color;
			}
		}

		return next;
	});
};
